import {
  Block,
  BlockChange,
  BlockType,
  isTypeForBlockWithChildren,
  BlockChangeWithConflict,
  isInstanceBlock,
  isListBlock,
  compareOrder,
  extractBaseOrder,
  isAgentBlock,
  isVirtualInstanceChildTarget,
} from '@basehub/data/replicache/index'
import { invariant } from '@epic-web/invariant'
import {
  AtLeast,
  TreeManager,
  TreeNode,
  getTreeManager,
} from '@basehub/utils/tree/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { areBlocksCompatible } from '@basehub/data/replicache/schema/block/compare'
import {
  IndexedMapCompoundIndex,
  createIndexedMap,
} from '@basehub/utils/indexed-map/index'
import {
  getBlockConflict,
  diffRichTextContent,
} from '@basehub/data/replicache/mutators/repo-space/commit/merge-helpers'
import type { JSONContent } from '@tiptap/core'
import uniqBy from 'lodash/uniqBy'

/* -------------------------------------------------------------------------------------------------
 * Get Diff Between Trees
 * -----------------------------------------------------------------------------------------------*/

export const nodesByParentKey = {
  updateIndex: ({ item, currentValue, oldItem, operation }) => {
    const parentMap =
      currentValue ?? (new Map() as NonNullable<typeof currentValue>)

    if (operation === 'upsert') {
      // Remove from old parent if it exists
      if (
        oldItem &&
        oldItem.parentKey &&
        oldItem.parentKey !== item.parentKey
      ) {
        const oldChildren = parentMap.get(oldItem.parentKey)
        if (oldChildren) {
          oldChildren.delete(item.key)
          if (oldChildren.size === 0) {
            parentMap.delete(oldItem.parentKey)
          }
        }
      }

      if (!item.parentKey) return parentMap

      // Add to new parent
      const children = parentMap.get(item.parentKey) ?? new Set<string>()
      children.add(item.key)
      parentMap.set(item.parentKey, children)
    } else {
      if (!item.parentKey) return parentMap

      const children = parentMap.get(item.parentKey)
      if (children) {
        children.delete(item.key)
        if (children.size === 0) {
          parentMap.delete(item.parentKey)
        }
      }
    }

    return parentMap
  },
  cloneIndex: (currentValue) => new Map(currentValue),
} satisfies IndexedMapCompoundIndex<TreeNode<Block>, Map<string, Set<string>>>

export type DiffBetweenTreesProps = {
  newTreeData: NonNullable<SerializableTreeData>
  oldTreeData: SerializableTreeData
  commonTreeData: SerializableTreeData
  sourceBlockIds: string[] | null // null meaning we'll check out the whole tree
  diffType: 'commit' | 'merge'
}

type SerializableTreeData =
  | { nodes: Array<TreeNode<Block>>; rootBlockId: string }
  | TreeManager<Block, NonNullable<unknown>>
  | null

const isTreeManager = (
  treeData: SerializableTreeData
): treeData is TreeManager<Block, NonNullable<unknown>> => {
  return !!treeData && 'itemsMap' in treeData
}

export type DiffBetweenTrees = {
  root: TreeNode<BlockChange>
  items: Array<TreeNode<BlockChange>>
  changesCount: number
  conflictsCount: number
}

const isConsideredPrimitiveForDiffView = (type: BlockType) =>
  !isTypeForBlockWithChildren(type) ||
  type === 'rich-text' ||
  type === 'reference'

function getChangesTreePathAndOrderPath({
  parentKey,
  order,
  treeNew,
  treeOld,
}: {
  parentKey: string | null
  order: string
  treeNew: TreeManager<Block, NonNullable<unknown>>
  treeOld: TreeManager<Block, NonNullable<unknown>> | null
}): { path: string[]; orderPath: string[] } {
  if (!parentKey) return { path: [], orderPath: [] }
  const parent = treeNew.getNode(parentKey) ?? treeOld?.getNode(parentKey)
  invariant(parent, `parent for ${parentKey} not found`)

  const resultUpTheTree = getChangesTreePathAndOrderPath({
    parentKey: parent.parentKey,
    order: parent.order,
    treeNew,
    treeOld,
  })

  return {
    // the sourceBlockId ensures that we can find blocks from the old tree
    path: [...resultUpTheTree.path, idToSourceBlockId(parent.value)],
    orderPath: [...resultUpTheTree.orderPath, order],
  }
}

export function getDiffBetweenTrees({
  commonTreeData,
  newTreeData,
  oldTreeData,
  diffType,
  sourceBlockIds,
}: DiffBetweenTreesProps): DiffBetweenTrees {
  const debugTimings = {
    now: performance.now(),
    init: 0,
    iterTreeNew: {
      now: 0,
      a: 0,
      b: 0,
      c: 0,
      total: 0,
    },
    iterMatchTree: 0,
    rest: 0,
    total: {
      now: performance.now(),
      value: 0,
    },
  }

  let changesCount = 0
  let conflictsCount = 0
  const countedBlocksSet = new Set<string>()

  let treeNew: null | TreeManager<Block, NonNullable<unknown>> = null
  let treeOld: null | TreeManager<Block, NonNullable<unknown>> = null
  let treeCommonAncestor: null | TreeManager<Block, NonNullable<unknown>> = null

  if (isTreeManager(newTreeData)) {
    treeNew = newTreeData
  }
  if (isTreeManager(oldTreeData)) {
    treeOld = oldTreeData
  }
  if (isTreeManager(commonTreeData)) {
    treeCommonAncestor = commonTreeData
  }

  if (!treeNew && !treeOld && !treeCommonAncestor) {
    invariant(!isTreeManager(newTreeData), 'newTreeData is a tree manager')
    invariant(!isTreeManager(oldTreeData), 'oldTreeData is a tree manager')
    invariant(
      !isTreeManager(commonTreeData),
      'commonTreeData is a tree manager'
    )

    const treeNewNodesMap = createIndexedMap<
      TreeNode<Block>,
      { byParentKey: typeof nodesByParentKey }
    >({
      compoundIndexes: {
        byParentKey: nodesByParentKey,
      },
    })
    const treeOldNodesMap = createIndexedMap<
      TreeNode<Block>,
      { byParentKey: typeof nodesByParentKey }
    >({
      compoundIndexes: {
        byParentKey: nodesByParentKey,
      },
    })
    const treeCommonAncestorNodesMap = createIndexedMap<
      TreeNode<Block>,
      { byParentKey: typeof nodesByParentKey }
    >({
      compoundIndexes: {
        byParentKey: nodesByParentKey,
      },
    })

    // Initialize maps
    for (
      let i = 0;
      i <
      Math.max(
        newTreeData.nodes.length,
        oldTreeData?.nodes.length ?? 0,
        commonTreeData?.nodes.length ?? 0
      );
      i++
    ) {
      const treeNewNode = newTreeData.nodes[i]
      const treeOldNode = oldTreeData?.nodes[i]
      const treeCommonAncestorNode = commonTreeData?.nodes[i]

      if (treeNewNode) {
        treeNewNodesMap.set(treeNewNode.key, treeNewNode)
      }

      if (treeOldNode) {
        treeOldNodesMap.set(treeOldNode.key, treeOldNode)
      }

      if (treeCommonAncestorNode) {
        treeCommonAncestorNodesMap.set(
          treeCommonAncestorNode.key,
          treeCommonAncestorNode
        )
      }
    }

    treeNew = getTreeManager<Block, NonNullable<unknown>>({
      getKey: (item) => item.id,
      getOrder: (key) => {
        const node = treeNewNodesMap.get(key)
        invariant(node, `node for ${key} not found`)
        return node.order
      },
      getChildrenKeys: (key) => {
        const index = treeNewNodesMap.compoundIndexes.get('byParentKey')
        const childrenKeys = index?.get(key)
        return Array.from(childrenKeys ?? [])
          .map((k) => treeNewNodesMap.get(k))
          .filter(Boolean)
          .sort(compareOrder)
          .map((n) => n.key)
      },
      rootKey: newTreeData.rootBlockId,
      getValue: (key) => {
        const block = treeNewNodesMap.get(key)?.value
        if (!block) {
          throw new Error(`block for ${key} not found`)
        }
        return block
      },
      indexKeys: ['sourceBlockId'],
      autoFallbackWithKeys: ['sourceBlockId'],
    })

    if (oldTreeData) {
      treeOld = getTreeManager<Block, NonNullable<unknown>>({
        getKey: (item) => item.id,
        getOrder: (key) => {
          const node = treeOldNodesMap.get(key)
          invariant(node, `node for ${key} not found`)
          return node.order
        },
        getChildrenKeys: (key) => {
          const index = treeOldNodesMap.compoundIndexes.get('byParentKey')
          const childrenKeys = index?.get(key)
          return Array.from(childrenKeys ?? [])
            .map((k) => treeOldNodesMap.get(k))
            .filter(Boolean)
            .sort(compareOrder)
            .map((n) => n.key)
        },
        rootKey: oldTreeData.rootBlockId,
        getValue: (key) => treeOldNodesMap.get(key)!.value,
        indexKeys: ['sourceBlockId'],
        autoFallbackWithKeys: ['sourceBlockId'],
      })
    }

    if (commonTreeData) {
      treeCommonAncestor = getTreeManager<Block, NonNullable<unknown>>({
        getKey: (item) => item.id,
        getOrder: (key) => {
          const node = treeCommonAncestorNodesMap.get(key)
          invariant(node, `node for ${key} not found`)
          return node.order
        },
        getChildrenKeys: (key) => {
          const index =
            treeCommonAncestorNodesMap.compoundIndexes.get('byParentKey')
          const childrenKeys = index?.get(key)
          return Array.from(childrenKeys ?? [])
            .map((k) => treeCommonAncestorNodesMap.get(k))
            .filter(Boolean)
            .sort(compareOrder)
            .map((n) => n.key)
        },
        rootKey: commonTreeData.rootBlockId,
        getValue: (key) => treeCommonAncestorNodesMap.get(key)!.value,
        indexKeys: ['sourceBlockId'],
        autoFallbackWithKeys: ['sourceBlockId'],
      })
    }
  }

  invariant(treeNew, 'treeNew should be defined by now')

  const matchTree =
    diffType === 'commit' ? treeOld : (treeCommonAncestor ?? treeOld)

  const rootKey = treeNew.root.value.sourceBlockId ?? treeNew.root.value.id
  const changesTreeManager = getTreeManager<BlockChange, NonNullable<unknown>>({
    rootKey,
    getValue: (key) => {
      if (key === rootKey) {
        return {
          title: treeNew.root.value.title,
          path: rootKey,
          id: rootKey,
          type: 'root',
          current: treeNew.root.value,
          previous: (matchTree?.root.value ?? null) as Block,
          conflicting: null,
          status: 'unmodified',
          children: [],
        }
      }
      throw new Error(
        `don't expect this to get called from nodes other than the root`
      )
    },
    getOrder: (key) => {
      if (key === rootKey) return treeNew.root.order
      throw new Error(
        `don't expect this to get called from nodes other than the root`
      )
    },
    getKey: (item) => item.id,
    getChildrenKeys: (_key, value) => {
      return (value.children?.map((child) => child) ?? [])
        .sort(compareOrder)
        .map((c) => c.id)
    },
  })

  debugTimings.init = performance.now() - debugTimings.now
  debugTimings.now = performance.now()

  // Process all nodes in a flat loop
  for (const node of treeNew.getAllNodes()) {
    debugTimings.iterTreeNew.now = performance.now()
    const sourceBlockId = node.value.sourceBlockId ?? node.value.id

    if (sourceBlockIds && !sourceBlockIds.includes(sourceBlockId)) {
      debugTimings.iterTreeNew.a +=
        performance.now() - debugTimings.iterTreeNew.now
      continue
    }

    const debug = false

    const block = node.value

    if (block.type === 'root') continue

    if (debug) {
      console.log('PROCESSING DEBUG BLOCK', block.id)
    }
    if (block.deletedAt) {
      // will be processed later on. shouldn't be here
      debugTimings.iterTreeNew.a +=
        performance.now() - debugTimings.iterTreeNew.now
      continue
    }
    const parent = node.parentKey
      ? treeNew.getNode(node.parentKey, { required: true })
      : null
    const match = matchTree?.getNode(block.sourceBlockId ?? block.id) ?? null
    const matchParent = match?.parentKey
      ? (matchTree?.getNode(match.parentKey) ?? null)
      : null

    // oldTree
    const matchInOldTree = treeOld?.getNode(block.sourceBlockId ?? block.id)
    const parentNodeInOldTree = matchInOldTree?.parentKey
      ? treeOld?.getNode(matchInOldTree.parentKey)
      : null

    // commonAncestor
    const commonAncestor = treeCommonAncestor?.getNode(
      block.sourceBlockId ?? block.id
    )
    const parentNodeInCommonAncestor = commonAncestor?.parentKey
      ? treeCommonAncestor?.getNode(commonAncestor.parentKey)
      : null

    if (debug) {
      console.log({ block, match, matchInOldTree })
    }

    debugTimings.iterTreeNew.a +=
      performance.now() - debugTimings.iterTreeNew.now
    debugTimings.iterTreeNew.now = performance.now()

    // Skip virtual blocks or treat them as unmodified
    if (
      // @ts-ignore
      block.__virtual
    ) {
      debugTimings.iterTreeNew.b +=
        performance.now() - debugTimings.iterTreeNew.now
      continue
    }

    let change: Partial<BlockChange> = {
      title: block.title,
      id: block.sourceBlockId ?? block.id,
      path: treeNew.getPath(block.id, { format: 'string', required: true }),
      type: block.type,
      current: block,
      conflicting: null,
      children: null,
    }

    change.previous = match?.value ?? null
    const isCreated = !match

    if (isCreated) {
      change.status = 'created'
    } else {
      let conflict
      if (diffType === 'merge') {
        conflict = getBlockConflict({
          sourceBR:
            node && parent
              ? {
                  blockId: block.id,
                  order: node.order,
                  parentId: idToSourceBlockId(parent.value) || '',
                  sourceBlockId: idToSourceBlockId(block),
                }
              : null,
          headBR:
            matchInOldTree && parentNodeInOldTree
              ? {
                  blockId: matchInOldTree.value.id,
                  order: matchInOldTree.order,
                  parentId: idToSourceBlockId(parentNodeInOldTree?.value) || '',
                  sourceBlockId: idToSourceBlockId(matchInOldTree.value),
                }
              : null,
          commonAncestorBR:
            commonAncestor && parentNodeInCommonAncestor
              ? {
                  blockId: commonAncestor.value.id,
                  order: commonAncestor.order,
                  parentId:
                    idToSourceBlockId(parentNodeInCommonAncestor?.value) || '',
                  sourceBlockId: idToSourceBlockId(commonAncestor.value),
                }
              : null,
          sourceBlock: block,
          headBlock: matchInOldTree?.value ?? null,
          resolveConflict: () => 'unresolved',
          commonAncestorBlock: commonAncestor?.value ?? null,
        })
      }

      // if reorder is different between the three trees, we'll need to handle it
      if (conflict) {
        if (conflict.type === 'reorder-in-both') {
          debugTimings.iterTreeNew.b +=
            performance.now() - debugTimings.iterTreeNew.now
          continue
        }
        conflictsCount++
        change = {
          ...change,
          status: 'conflict',
          conflicting: matchInOldTree?.value ?? null,
          conflictType:
            conflict.type as BlockChangeWithConflict['conflictType'],
          conflictPaths:
            conflict.conflictPaths as BlockChangeWithConflict['conflictPaths'],
          conflictValues:
            conflict.conflictValues as BlockChangeWithConflict['conflictValues'],
        } as unknown as BlockChangeWithConflict
      } else if (
        !matchParent ||
        !parent ||
        idToSourceBlockId(matchParent.value) !== idToSourceBlockId(parent.value)
      ) {
        change.status = 'reordered'
      } else if (!areBlocksCompatible(match.value, block, { debug })) {
        change.status = 'modified'
      } else {
        change.status = 'unmodified'
      }
    }

    debugTimings.iterTreeNew.b +=
      performance.now() - debugTimings.iterTreeNew.now
    debugTimings.iterTreeNew.now = performance.now()

    if (change.status === 'unmodified') {
      // check if it was reordered / re-parented
      if (matchParent && parent) {
        if (
          idToSourceBlockId(matchParent.value) !==
          idToSourceBlockId(parent.value)
        ) {
          // re-parented
          change.status = 'reordered'
        }
        if (
          match &&
          extractBaseOrder(node.order) !== extractBaseOrder(match.order)
        ) {
          // re-ordered? maybe... we should also check if the "resolved index" is different.
          // the user might've reordered and then reverted the reorder back.
          const indexOfMatch = matchTree
            ?.getChildrenKeys(matchParent.key)
            .indexOf(match.key)
          const indexOfNode = treeNew
            .getChildrenKeys(parent.key)
            .indexOf(node.key)
          if (indexOfMatch !== indexOfNode) {
            change.status = 'reordered'
          }
        }
      }
    }

    if (debug) {
      console.log({ change })
    }

    const parsedChange = change as BlockChange
    if (parsedChange.status === 'unmodified') {
      debugTimings.iterTreeNew.c +=
        performance.now() - debugTimings.iterTreeNew.now
      continue
    }

    changesTreeManager.addNode(parsedChange, {
      parentKey: node.parentKey,
      order: node.order,
    })

    debugTimings.iterTreeNew.c +=
      performance.now() - debugTimings.iterTreeNew.now
    debugTimings.iterTreeNew.now = performance.now()
  }

  debugTimings.iterTreeNew.total = performance.now() - debugTimings.now
  debugTimings.now = performance.now()

  // Process deleted nodes
  if (matchTree) {
    for (const node of matchTree.getAllNodes()) {
      if (
        sourceBlockIds &&
        !sourceBlockIds.includes(node.value.sourceBlockId ?? node.value.id)
      ) {
        continue
      }

      if (isVirtualInstanceChildTarget(node.value)) {
        continue
      }

      const block = node.value
      const isDeleted = !treeNew.getNode(idToSourceBlockId(block))

      if (!isDeleted) continue

      try {
        matchTree.getPath(block.id, { format: 'string', required: true })
      } catch (err) {
        // ignore. block is detached or something...
        continue
      }

      const deletedNode: BlockChange = {
        title: block.title,
        id: block.sourceBlockId ?? block.id,
        path: matchTree.getPath(block.id, { format: 'string', required: true }),
        type: block.type,
        status: 'deleted',
        previous: block,
        current: null,
        conflicting: null,
        children: [],
      }

      const matchInOldTreeNode =
        treeOld?.getNode(block.sourceBlockId ?? block.id) ?? null
      const parentNodeInOldTree = matchInOldTreeNode?.parentKey
        ? treeOld?.getNode(matchInOldTreeNode.parentKey)
        : null
      const matchInOldTree = matchInOldTreeNode?.value ?? null
      const commonAncestorNode =
        treeCommonAncestor?.getNode(block.sourceBlockId ?? block.id) ?? null
      const parentNodeInCommonAncestor = commonAncestorNode?.parentKey
        ? treeCommonAncestor?.getNode(commonAncestorNode.parentKey)
        : null
      const commonAncestor = commonAncestorNode?.value ?? null

      const conflict = getBlockConflict({
        sourceBR: null,
        headBR:
          matchInOldTreeNode && parentNodeInOldTree
            ? {
                blockId: matchInOldTreeNode.value.id,
                order: matchInOldTreeNode.order,
                parentId: idToSourceBlockId(parentNodeInOldTree?.value) || '',
                sourceBlockId: idToSourceBlockId(matchInOldTreeNode.value),
              }
            : null,
        commonAncestorBR:
          commonAncestorNode && parentNodeInCommonAncestor
            ? {
                blockId: commonAncestorNode.value.id,
                order: commonAncestorNode.order,
                parentId:
                  idToSourceBlockId(parentNodeInCommonAncestor?.value) || '',
                sourceBlockId: idToSourceBlockId(commonAncestorNode.value),
              }
            : null,
        sourceBlock: null,
        headBlock: matchInOldTree ?? null,
        resolveConflict: () => 'unresolved',
        commonAncestorBlock: commonAncestor,
      })

      if (conflict) {
        if (conflict.type === 'reorder-in-both') continue
        conflictsCount++
        Object.assign(deletedNode, {
          conflicting: matchInOldTree ?? null,
          status: 'conflict',
          conflictType:
            conflict.type as BlockChangeWithConflict['conflictType'],
          conflictPaths:
            conflict.conflictPaths as BlockChangeWithConflict['conflictPaths'],
          conflictValues:
            conflict.conflictValues as BlockChangeWithConflict['conflictValues'],
        })
      }

      changesTreeManager.addNode(deletedNode, {
        parentKey: node.parentKey,
        order: node.order,
      })
    }
  }

  debugTimings.iterMatchTree = performance.now() - debugTimings.now
  debugTimings.now = performance.now()

  // After processing all nodes, ensure proper parent attachment for all nodes in changes tree
  const nodesToProcess = Array.from(changesTreeManager.getAllNodes())
  for (const node of nodesToProcess) {
    const change = node.value
    if (change.type === 'root') continue

    const debug = false

    if (debug) {
      console.log(`PROCESSING POSSIBLY DELETED BLOCK`, change)
    }

    const keyThatCounts = isConsideredPrimitiveForDiffView(node.value.type)
      ? node.parentKey
      : node.value.id
    if (keyThatCounts && !countedBlocksSet.has(keyThatCounts)) {
      changesCount++
      countedBlocksSet.add(keyThatCounts)
    }

    // For deleted nodes, we need to get the parent from matchTree
    // For other nodes, we get it from treeNew
    const isDeleted =
      change.status === 'deleted' ||
      (change.status === 'conflict' &&
        change.conflictType === 'deleted-in-source')
    const parentKey = node.parentKey
    invariant(parentKey, `parent key for ${change.id} not found`)

    const parentNode = isDeleted
      ? matchTree?.getNode(parentKey, { required: true })
      : treeNew.getNode(parentKey, { required: true })
    invariant(parentNode, `parent node for ${change.id} not found`)

    const parentSourceBlockId = idToSourceBlockId(parentNode.value)
    const parentInChangesTree = changesTreeManager.getNode(parentSourceBlockId)
    const parentPath = isDeleted
      ? matchTree?.getPath(parentSourceBlockId, {
          format: 'string',
        })
      : treeNew.getPath(parentSourceBlockId)
    invariant(parentPath, `parent path for ${change.id} not found`)

    if (debug) {
      console.log({
        parentInChangesTree,
        parentNode,
        parentSourceBlockId,
      })
    }

    if (parentInChangesTree) {
      // already attached, though we'll need to update its `children` array
      parentInChangesTree.value.children = uniqBy(
        [
          ...(parentInChangesTree.value.children ?? []),
          {
            id: node.value.id,
            status: node.value.status,
            order: node.order,
            conflictType: (node.value as any).conflictType,
            conflictPaths: (node.value as any).conflictPaths,
          },
        ],
        'id'
      ).sort(compareOrder)
      continue
    }

    if (debug) {
      console.log({
        parentInChangesTree,
        parentNode,
        parentSourceBlockId,
      })
    }

    let nodeThatNeedsAttachment = node

    if (isConsideredPrimitiveForDiffView(nodeThatNeedsAttachment.value.type)) {
      // ensure parent is in changes tree, be it unmodified or not
      // we're sure the parent is 'unmodified' in this case, otherwise it'd be here already
      // we'll add it as 'modified' though, cause a primitive block changed.
      const parentInTreeNew = treeNew.getNode(parentSourceBlockId)
      const parentPathInTreeNew = parentInTreeNew
        ? treeNew.getPath(parentSourceBlockId, {
            format: 'string',
          })
        : null
      changesTreeManager.addNode(
        {
          type: parentInTreeNew?.value.type || parentNode.value.type,
          status: parentInTreeNew ? 'modified' : 'deleted',
          previous: parentNode.value,
          current: parentInTreeNew?.value ?? null,
          id: parentSourceBlockId,
          title: parentInTreeNew?.value.title || parentNode.value.title,
          path: parentPathInTreeNew || parentPath,
          children: [
            {
              id: node.value.id,
              status: node.value.status,
              order: node.order,
              conflictType: (node.value as any).conflictType,
              conflictPaths: (node.value as any).conflictPaths,
            },
          ],
          conflicting: null,
        },
        {
          parentKey: parentInTreeNew?.parentKey || parentNode.parentKey,
          order: parentInTreeNew?.order || parentNode.order,
        }
      )
      nodeThatNeedsAttachment = changesTreeManager.getNode(
        parentSourceBlockId,
        { required: true }
      )
    } else if ((node.value as any).conflictType === 'reorder-in-both') {
      const parentInTreeNew = treeNew.getNode(parentSourceBlockId, {
        required: true,
      })
      const parentPathInTreeNew = treeNew.getPath(parentSourceBlockId, {
        format: 'string',
        required: true,
      })
      changesTreeManager.addNode(
        {
          type: parentInTreeNew.value.type,
          status: 'modified',
          previous: parentInTreeNew.value,
          current: parentInTreeNew.value,
          id: parentSourceBlockId,
          title: parentInTreeNew.value.title,
          path: parentPathInTreeNew,
          children: [
            {
              id: node.value.id,
              status: 'reordered-source',
              order: node.order,
              conflictType: (node.value as any).conflictType,
              conflictPaths: (node.value as any).conflictPaths,
            },
          ],
          conflicting: null,
        },
        {
          parentKey: parentInTreeNew.parentKey,
          order: parentInTreeNew.order,
        }
      )
      nodeThatNeedsAttachment = changesTreeManager.getNode(
        parentSourceBlockId,
        { required: true }
      )
    }

    if (debug) {
      console.log({ nodeThatNeedsAttachment })
    }

    // now attach to the correct parent
    const { path: nodePath, orderPath: nodeOrderPath } =
      getChangesTreePathAndOrderPath({
        parentKey: parentSourceBlockId,
        order: nodeThatNeedsAttachment.order,
        treeNew,
        treeOld,
      })

    /**
     * Ensure parent is ALWAYS in the changes tree if we're dealing with a primitive node (which won't render in the sidebar by itself).
     * Now, where should we attach the new parent node? By default, in the root, unless some ancestor is already in the changes tree.
     * If an ancestor is already in the changes tree, we must ensure all the ancestors up until this first ancestor are also in the changes tree.
     * We grab the parent node's CURRENT path (why current? well, we care about the current more than the previous. if there's no current, it means it was deleted and we shouldn't be here anyways (nested updateParent = false)).
     */
    let attachTo = changesTreeManager.root.key
    let attachWithOrder = nodeOrderPath.join('')
    let ensureNextOnesAreInChangesTree = false
    let prevKey: string | null = null
    nodePath.forEach((segment) => {
      if (
        segment === nodeThatNeedsAttachment.value.id &&
        attachTo !== changesTreeManager.root.key
      ) {
        // means we found a parent other than the root to attach to
        // so we'll reset the attachWithOrder to be the original order
        attachWithOrder = nodeThatNeedsAttachment.order
        return
      }

      const currentNode =
        treeNew.getNode(segment) ?? matchTree?.getNode(segment)
      invariant(currentNode, `current node for ${segment} not found`)

      if (
        currentNode.value.type === 'root' ||
        idToSourceBlockId(currentNode.value) ===
          nodeThatNeedsAttachment.value.id
      ) {
        return
      }
      const nodeInChangesTree = changesTreeManager.getNode(
        idToSourceBlockId(currentNode.value)
      )
      if (nodeInChangesTree) {
        attachTo = nodeInChangesTree.key
        attachWithOrder = nodeOrderPath
          .slice(nodeOrderPath.indexOf(segment))
          .join('')
        ensureNextOnesAreInChangesTree = true
        prevKey = nodeInChangesTree.key
      } else if (ensureNextOnesAreInChangesTree && prevKey) {
        // this means that some ancestor was in the changes tree, and now we need to ensure nesting is correct
        const newBlockChange: BlockChange = {
          type: currentNode.value.type,
          status: 'unmodified',
          previous:
            // cause "unmodified"
            currentNode.value,
          current: currentNode.value,
          id: idToSourceBlockId(currentNode.value),
          title: currentNode.value.title,
          path:
            treeNew.getPath(idToSourceBlockId(currentNode.value)) ??
            matchTree?.getPath(idToSourceBlockId(currentNode.value)) ??
            '',
          children: [],
          conflicting: null,
        }
        changesTreeManager.addNode(newBlockChange, {
          parentKey: prevKey,
          order: node.order,
        })
        changesTreeManager.updateNode(prevKey, {
          value: {
            ...changesTreeManager.getNode(prevKey, { required: true }).value,
            children: uniqBy(
              [
                ...(changesTreeManager.getNode(prevKey, { required: true })
                  .value.children ?? []),
                {
                  id: newBlockChange.id,
                  order: node.order,
                  status: newBlockChange.status,
                  conflictType: (newBlockChange as any).conflictType,
                  conflictPaths: (newBlockChange as any).conflictPaths,
                },
              ],
              'id'
            ).sort(compareOrder),
          },
        })
        attachTo = newBlockChange.id
        attachWithOrder = nodeOrderPath
          .slice(nodeOrderPath.indexOf(segment))
          .join('')
        prevKey = newBlockChange.id
      }
    })

    if (debug) {
      console.log({ attachTo })
    }

    changesTreeManager.updateNode(nodeThatNeedsAttachment.key, {
      parentKey: attachTo,
      order: attachWithOrder,
    })
    changesTreeManager.updateNode(attachTo, {
      value: {
        ...changesTreeManager.getNode(attachTo, { required: true }).value,
        children: uniqBy(
          [
            ...(changesTreeManager.getNode(attachTo, {
              required: true,
            }).value.children ?? []),
            {
              id: nodeThatNeedsAttachment.value.id,
              order: nodeThatNeedsAttachment.order,
              status: nodeThatNeedsAttachment.value.status,
              conflictType: (nodeThatNeedsAttachment.value as any).conflictType,
              conflictPaths: (nodeThatNeedsAttachment.value as any)
                .conflictPaths,
            },
          ],
          'id'
        ).sort(compareOrder),
      },
    })
  }

  const items: Array<TreeNode<BlockChange>> = []
  for (const node of changesTreeManager.getAllNodes()) {
    items.push(node)
  }

  debugTimings.rest = performance.now() - debugTimings.now
  debugTimings.total.value = performance.now() - debugTimings.total.now

  return { root: changesTreeManager.root, items, changesCount, conflictsCount }
}

/* -------------------------------------------------------------------------------------------------
 * Get Diff Between Rich Texts
 * -----------------------------------------------------------------------------------------------*/

export type DiffBetweenRichTextsProps = {
  a: JSONContent[]
  b: JSONContent[]
}

export type DiffBetweenRichTexts = ReturnType<typeof diffRichTextContent>

export function getDiffBetweenRichTexts({
  a,
  b,
}: DiffBetweenRichTextsProps): DiffBetweenRichTexts {
  return diffRichTextContent(a, b)
}

/* -------------------------------------------------------------------------------------------------
 * Should Show Change In Sidebar
 * -----------------------------------------------------------------------------------------------*/

export function shouldShowChangeInSidebar(
  change: BlockChange,
  parent?: AtLeast<BlockChange, 'id' | 'type' | 'path' | 'current' | 'previous'>
) {
  if (isAgentBlock(change.current ?? change.previous!)) {
    return true
  }

  if (
    parent &&
    !isListBlock(change.current ?? change.previous!) &&
    isInstanceBlock(parent.current ?? parent.previous!) &&
    parent.status === 'unmodified'
  ) {
    return false
  }

  if (
    parent &&
    isListBlock(change.current ?? change.previous!) &&
    isInstanceBlock(parent.current ?? parent.previous!) &&
    parent.status === 'unmodified' &&
    !change.nestedPrimitiveChanged
  ) {
    return false
  }

  return isTypeForBlockWithChildren(change.type) && change.type !== 'root'
}
